import React, { ReactNode, SyntheticEvent } from 'react'
import classNames from 'classnames'
import ImageLoader from '../image-loader/ImageLoader'

import './Card.less'
import { Box, ButtonPrimary, LinkOverlay } from 'front-end/src/components'
import { radii } from '../../chakraThemeExtension'
import IconArrowRight from '../icon/IconArrowRight'
import { useLooopTheme } from '../../../public-api/hooks/useLooopTheme'
import { brandColors } from '../../../../../shared/basicPalette'
import IconLock from '../icon/IconLock'
import { Trans } from '@lingui/macro'
import { Circle } from '@chakra-ui/react'

export interface ICardProps {
  children: any
  image?: string | null
  imageSrcSet?: string | null
  className?: string
  onClick?: (val: SyntheticEvent<any>) => void
  offsetTop?: boolean
  disabled?: boolean
  linkUrl?: string
  isLocked?: boolean
  isFailed?: boolean
  ctaButton?: ReactNode | string
  lockMessage?: ReactNode | string
}

export const Card: React.FC<ICardProps> = ({
  children,
  isLocked = false,
  isFailed = false,
  className,
  disabled,
  linkUrl,
  offsetTop,
  ctaButton,
  onClick,
  image,
  imageSrcSet,
  lockMessage = <Trans>Resource locked</Trans>
}) => {
  const { theme } = useLooopTheme()

  const card = (
    <Box
      cursor={!disabled ? 'pointer' : 'not-allowed'}
      pt={offsetTop ? '20px' : undefined}
      onClick={!disabled ? onClick : undefined}
      className={classNames('Card', { 'Card__has-image': image }, className)}
      boxShadow={
        ctaButton
          ? `0 0 0 2px ${theme.colors.buttonPrimary.default},0 8px 16px rgb(8 16 24 / 10%)`
          : undefined
      }
    >
      <Box opacity={disabled ? '0.5' : undefined}>
        {image && (
          <div className="Card__image">
            <ImageLoader
              alt="Card Image"
              src={image}
              srcSet={imageSrcSet}
              preloaderWrapper={<div className="Card__spinner" />}
            />
          </div>
        )}
        {children}
      </Box>

      {isLocked && (
        <Circle
          flexDirection="column"
          boxSize="128px"
          p="20px"
          position="absolute"
          top="50%"
          left="50%"
          textAlign="center"
          lineHeight="16px"
          fontSize="sm"
          transform="translate(-50%, -50%)"
          color={isFailed ? brandColors.white : brandColors.grey.mid}
          bgColor={isFailed ? brandColors.red.mid : brandColors.white}
          boxShadow="0 2px 27px 0 rgba(0, 0, 0, 0.2)"
        >
          <IconLock fontSize="45px" mb="8px" /> {lockMessage}
        </Circle>
      )}

      {ctaButton && (
        <ButtonPrimary
          position="absolute"
          right={0}
          bottom={0}
          borderBottomLeftRadius={0}
          borderTopRightRadius={0}
          pr={'8px'}
          tabIndex={-1}
          rightIcon={<IconArrowRight />}
        >
          {ctaButton}
        </ButtonPrimary>
      )}
    </Box>
  )

  return disabled || !linkUrl ? (
    card
  ) : (
    <LinkOverlay
      h="100%"
      display="block"
      to={linkUrl}
      borderRadius={radii.lg}
      transition="all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1)"
      _hover={{
        transform: 'scale3d(1.01333, 1.01333, 1) translateY(-2px)',
        boxShadow: '0 8px 16px rgba(8, 16, 24, 0.1)'
      }}
      _focusVisible={{
        boxShadow: 'var(--chakra-shadows-focus)'
      }}
    >
      {card}
    </LinkOverlay>
  )
}

export default Card
